<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { ProductDetail } from "../api/products";
import { listServiceTemplates, saveServiceBinding, saveServiceTemplate, serviceFields,
  type ServiceField, type ServiceTemplate } from "../api/service-templates";

const props = defineProps<{ product: ProductDetail; dirty: boolean; disabled: boolean }>();
const emit = defineEmits<{ saved: [product: ProductDetail] }>();
const templates = ref<ServiceTemplate[]>([]);
const enabled = ref(false);
const loading = ref(true);
const busy = ref(false);
const message = ref("");
const chosenId = ref("");
const inherit = ref<ServiceField[]>([]);
const editing = ref<ServiceTemplate | null>(null);
const creating = ref(false);
const acknowledged = ref(false);
const chosen = computed(() => templates.value.find((template) => template.id === chosenId.value));

function resetBinding() {
  chosenId.value = props.product.serviceTemplate?.templateId ?? "";
  inherit.value = [...(props.product.serviceTemplate?.inheritFields ?? [])];
}
watch(() => props.product, resetBinding, { immediate: true });
async function reload() {
  loading.value = true;
  try {
    const result = await listServiceTemplates();
    enabled.value = result.enabled;
    templates.value = result.items;
  } catch (error) { message.value = error instanceof Error ? error.message : "模板读取失败，请刷新重试"; }
  finally { loading.value = false; }
}
async function run(work: () => Promise<void>) {
  busy.value = true;
  message.value = "";
  try { await work(); }
  catch (error) { message.value = error instanceof Error ? error.message : "保存结果未知，请刷新核对后再操作"; }
  finally { busy.value = false; }
}
function chooseTemplate() {
  // Changing template never silently opts an existing independent field into inheritance.
  inherit.value = [];
}
async function saveBinding() {
  await run(async () => {
    if (props.dirty) throw new Error("请先保存商品服务信息，再更改模板关联");
    if (chosenId.value && !chosen.value) throw new Error("模板不可用，请刷新模板列表");
    const product = await saveServiceBinding(props.product, chosen.value, inherit.value);
    emit("saved", product);
    await reload();
    message.value = "关联已保存；原独立文本保留，解除关联时恢复。";
  });
}
function editTemplate(template?: ServiceTemplate) {
  creating.value = !template;
  editing.value = template ? { ...template, fields: { ...template.fields }, linkedProducts: template.linkedProducts.map(item => ({ ...item })) } : {
    id: crypto.randomUUID(), name: "", active: true, version: 1, linkedProducts: [],
    fields: { shippingLeadTime: null, shippingDescription: null, freeShippingDescription: null,
      guaranteeDescription: null, invoiceDescription: null },
  };
  acknowledged.value = false;
}
async function saveTemplate() {
  await run(async () => {
    const template = editing.value;
    if (!template || !template.name.trim()) throw new Error("请填写模板名称");
    if (template.linkedProducts.length && !acknowledged.value) throw new Error("请核对可能受影响的商品并确认");
    await saveServiceTemplate(template, creating.value);
    editing.value = null;
    await reload();
    message.value = "模板已保存；关联商品下次读取时采用最新的未覆盖字段。";
  });
}
onMounted(reload);
</script>

<template>
  <div class="service-templates">
    <p
      v-if="loading"
      role="status"
    >
      正在读取服务模板…
    </p>
    <p v-else-if="!enabled">
      定制服务模板尚未启用；当前继续使用商品独立服务信息。
    </p>
    <p
      v-if="message"
      role="status"
      aria-live="polite"
    >
      {{ message }}
    </p>
    <template v-if="enabled">
      <p
        v-if="product.serviceTemplate?.available === false"
        role="alert"
      >
        已关联模板不可用，小程序读取已阻止。请恢复模板或明确解除关联；原独立文本仍保留。
      </p>
      <fieldset :disabled="busy || disabled || loading">
        <legend>服务模板与字段来源</legend>
        <label class="field">
          <span>关联模板</span>
          <select
            v-model="chosenId"
            @change="chooseTemplate"
          >
            <option value="">不使用模板（恢复原独立文本）</option>
            <option
              v-for="template in templates"
              :key="template.id"
              :value="template.id"
              :disabled="!template.active"
            >
              {{ template.name }}{{ template.active ? '' : '（已停用）' }}
            </option>
          </select>
        </label>
        <p>选中“继承模板”才使用公共文案；未选字段为单商品覆盖，在下方原表单编辑。原独立文本始终保留。</p>
        <div
          v-for="field in serviceFields"
          :key="field.key"
          class="source-row"
        >
          <label>
            <input
              v-model="inherit"
              type="checkbox"
              :value="field.key"
              :disabled="!chosen"
            >
            {{ field.label }} · {{ inherit.includes(field.key) && chosen ? '保存后继承模板' : '保存后单商品独立 / 覆盖' }}
          </label>
          <p>当前生效（{{ product.serviceTemplate?.inheritFields.includes(field.key) ? '来自模板' : '商品独立值' }}）：{{ (product.serviceTemplate ? product.serviceTemplate.effective[field.key] : product[field.key]) || '未填写：沿用保守说明，发票按实际配置显示' }}</p>
          <p>保存后预览：{{ (inherit.includes(field.key) && chosen ? chosen.fields[field.key] : product[field.key]) || '未填写：小程序沿用保守说明，发票入口按实际配置显示' }}</p>
        </div>
        <p v-if="dirty">
          服务信息有未保存修改，请先保存下方商品参数。
        </p>
        <button
          type="button"
          :disabled="dirty"
          @click="saveBinding"
        >
          保存关联与字段来源
        </button>
      </fieldset>

      <details>
        <summary>管理定制服务公共模板</summary>
        <p>只共享服务文案。运费说明不参与计价，不自动关联商品。</p>
        <button
          type="button"
          :disabled="busy || disabled"
          @click="editTemplate()"
        >
          新建模板
        </button>
        <ul>
          <li
            v-for="template in templates"
            :key="template.id"
          >
            {{ template.name }} · {{ template.active ? '启用' : '停用' }} · 关联 {{ template.linkedProducts.length }} 个商品
            <button
              type="button"
              :disabled="busy || disabled"
              @click="editTemplate(template)"
            >
              查看 / 编辑
            </button>
          </li>
        </ul>
        <p v-if="!templates.length">
          暂无模板，可先新建，再为商品显式关联。
        </p>
        <fieldset
          v-if="editing"
          :disabled="busy || disabled"
        >
          <legend>{{ creating ? '新建模板' : '编辑模板' }}</legend>
          <label class="field"><span>模板名称</span><input
            v-model="editing.name"
            maxlength="100"
            required
          ></label>
          <label
            v-for="field in serviceFields"
            :key="field.key"
            class="field"
          >
            <span>{{ field.label }}</span>
            <textarea
              v-model="editing.fields[field.key]"
              :maxlength="field.max"
              rows="2"
            />
          </label>
          <label><input
            v-model="editing.active"
            type="checkbox"
          >启用模板（仍有关联时不能停用）</label>
          <p v-if="editing.linkedProducts.length">
            可能影响：{{ editing.linkedProducts.map(item => `${item.name}（${item.id}）`).join('、') }}。
            仅未覆盖字段下次读取时更新；正在被继承的非空字段不能清空。
          </p>
          <label v-if="editing.linkedProducts.length"><input
            v-model="acknowledged"
            type="checkbox"
          >已核对上述商品，确认更新共享文案</label>
          <div class="actions">
            <button
              type="button"
              @click="saveTemplate"
            >
              保存模板
            </button>
            <button
              type="button"
              @click="editing = null"
            >
              取消编辑
            </button>
          </div>
        </fieldset>
      </details>
    </template>
    <button
      type="button"
      :disabled="busy || disabled || loading"
      @click="reload"
    >
      刷新模板列表
    </button>
  </div>
</template>

<style scoped>
.service-templates { display: grid; gap: 16px; margin-bottom: 24px; color: var(--admin-ink); }
fieldset { min-width: 0; padding: 16px; border: 1px solid var(--admin-outline-soft); border-radius: 12px; }
legend { font-weight: 600; padding: 0 8px; }
.field { display: grid; gap: 8px; margin: 16px 0; }
input:not([type=checkbox]), select, textarea { box-sizing: border-box; width: 100%; padding: 8px 12px; border: 1px solid var(--admin-outline); border-radius: 8px; font: inherit; }
textarea { resize: vertical; }
input[type=checkbox] { margin-right: 8px; accent-color: var(--admin-primary); }
.source-row { margin: 16px 0; }
p { margin: 8px 0; line-height: 1.6; overflow-wrap: anywhere; white-space: pre-wrap; }
button { padding: 8px 16px; border: 1px solid var(--admin-outline); border-radius: 8px; background: var(--admin-surface); color: inherit; font: inherit; cursor: pointer; }
button:hover:not(:disabled) { background: var(--admin-background); }
button:disabled { opacity: .55; cursor: default; }
:is(button, input, select, textarea, summary):focus-visible { outline: 2px solid var(--admin-primary-deep); outline-offset: 2px; }
summary { cursor: pointer; font-weight: 600; }
ul { padding-left: 24px; }
li { margin: 16px 0; overflow-wrap: anywhere; }
li button { margin-left: 8px; }
.actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
</style>
